/**
 * useFakeLive — polls /api/v1/live/counters for the LiveStrip.
 *
 * Per CEO directive 2026-04-28 (no mock anywhere): on backend failure the
 * hook keeps the previous successful values rather than fabricating jittered
 * seed numbers. If we never had a successful response, every counter stays
 * `{ value: null, ok: false }` so LiveStrip renders an empty strip rather
 * than a happy-looking but fake one.
 *
 * Returns a map of counterId → { value, ok, reason? } updated every 10 s.
 * Pauses while the tab is hidden (visibilitychange API) and fires one
 * immediate refresh on focus return.
 *
 * Counters that the backend can't compute (real-time presence/session) come
 * back with `ok: false` and LiveStrip hides them automatically.
 */
import { useEffect, useState } from "react";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4000/api/v1";
const POLL_MS = 10000;

// Catalog of counter ids the strip can render. Used only to pre-populate the
// initial state with `ok: false` placeholders so React renders nothing until
// the first live response arrives.
const KNOWN_IDS = [
  "today_revenue", "calls_in_progress", "brand_calls_live", "fan_calls_live",
  "live_rooms", "fans_online", "creators_online", "brands_online",
  "signups_today", "refunds_today", "pending_payouts", "tickets_open",
];

async function fetchLive(controller) {
  const token  = (typeof localStorage !== "undefined") ? localStorage.getItem("fc_token") : null;
  const apiKey = process.env.REACT_APP_API_KEY;
  const headers = {};
  if (token)       headers.Authorization = `Bearer ${token}`;
  else if (apiKey) headers["X-API-Key"]  = apiKey;
  const res = await fetch(`${API_BASE}/live/counters`, {
    headers,
    signal: controller.signal,
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  if (!json || !json.success || !json.data || !json.data.counters) {
    throw new Error("malformed live counters envelope");
  }
  return json.data.counters;
}

export function useFakeLive() {
  const [counters, setCounters] = useState(() => {
    const out = {};
    KNOWN_IDS.forEach((k) => { out[k] = { value: null, ok: false }; });
    return out;
  });

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    let timer = null;
    let controller = new AbortController();
    let stopped = false;

    const tick = async () => {
      controller = new AbortController();
      try {
        const live = await fetchLive(controller);
        if (stopped) return;
        // Backend is the single source of truth — copy whatever it returned,
        // including {ok:false} entries, so the strip honours real availability.
        setCounters(live);
      } catch (e) {
        // Network/abort error — keep the last successful values. If we never
        // had a successful poll the placeholders stay `ok:false` and the
        // strip renders empty (the existing behaviour for unsupported counters).
        if (stopped) return;
      }
    };

    const start = () => {
      stop();
      tick();   // immediate first fetch
      timer = setInterval(tick, POLL_MS);
    };
    const stop = () => {
      if (timer) { clearInterval(timer); timer = null; }
      try { controller.abort(); } catch (e) { /* ignore */ }
    };
    const onVisibility = () => {
      if (document.hidden) stop();
      else start();
    };

    start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stopped = true;
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return counters;
}

export const COUNTER_FORMAT_HINT = {
  today_revenue:   "money",
  pending_payouts: "money",
};
